import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import type { Rng } from "../rng";
import { flat, Tone } from "../materials";
import { Slot, slotNormal } from "../palette";
import { blobGeometry } from "../geometry";

export type LocalSurface =
  | { kind: "disc"; x: number; z: number; radius: number; height: number }
  | { kind: "rect"; x: number; z: number; halfWidth: number; halfDepth: number; height: number; yaw: number };

export interface Building {
  group: THREE.Group;
  label: string;
  x: number;
  z: number;
  radius: number;
  surfaces: LocalSurface[];
}

const MARBLE = Tone.ash;
const WEATHERED = Tone.stone;
const DARK_STONE = Tone.slate;

function asIndexed(geometry: THREE.BufferGeometry): THREE.BufferGeometry {
  if (geometry.index) return geometry;
  const count = geometry.getAttribute("position").count;
  const index = new Array<number>(count);
  for (let i = 0; i < count; i++) index[i] = i;
  geometry.setIndex(index);
  return geometry;
}

class Ruin {
  private readonly buckets = new Map<string, { tone: number; slot: number; geometries: THREE.BufferGeometry[] }>();

  add(geometry: THREE.BufferGeometry, tone: number, slot: number): void {
    const key = `${tone}:${slot}`;
    let bucket = this.buckets.get(key);
    if (!bucket) {
      bucket = { tone, slot, geometries: [] };
      this.buckets.set(key, bucket);
    }
    const solid = asIndexed(geometry);
    solid.deleteAttribute("uv");
    solid.deleteAttribute("uv1");
    bucket.geometries.push(solid);
  }

  build(variant: number): THREE.Group {
    const group = new THREE.Group();
    for (const bucket of this.buckets.values()) {
      const merged = mergeGeometries(bucket.geometries, false);
      if (!merged) continue;
      const mesh = new THREE.Mesh(merged, flat(bucket.tone));
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      mesh.userData.slot = slotNormal(bucket.slot);
      mesh.userData.variant = bucket.slot === Slot.foliage ? variant : 0.5;
      if (bucket.slot === Slot.foliage) mesh.layers.enable(1);
      group.add(mesh);
    }
    return group;
  }
}

function block(
  parts: Ruin,
  w: number,
  h: number,
  d: number,
  tone: number,
  slot: number,
  x: number,
  y: number,
  z: number,
  yaw = 0,
): void {
  const geometry = new THREE.BoxGeometry(w, h, d);
  if (yaw !== 0) geometry.rotateY(yaw);
  geometry.translate(x, y, z);
  parts.add(geometry, tone, slot);
}

function shaft(
  parts: Ruin,
  x: number,
  y: number,
  z: number,
  height: number,
  radius: number,
  tone: number,
  slice = 1,
): void {
  const geometry =
    slice < 1
      ? new THREE.CylinderGeometry(radius * 0.86, radius, height, 10, 1, true, 0, Math.PI * 2 * slice)
      : new THREE.CylinderGeometry(radius * 0.86, radius, height, 10, 1, true);
  geometry.translate(x, y + height / 2, z);
  parts.add(geometry, tone, Slot.stone);
}

function moss(
  parts: Ruin,
  rng: Rng,
  x: number,
  y: number,
  z: number,
  spread: number,
  count: number,
): void {
  for (let i = 0; i < count; i++) {
    const radius = rng.range(0.18, 0.42);
    const blob = blobGeometry(radius, 1, 0.34, rng);
    blob.scale(1, rng.range(0.4, 0.7), 1);
    blob.translate(
      x + rng.range(-spread, spread),
      y + rng.range(-0.06, 0.16),
      z + rng.range(-spread, spread),
    );
    parts.add(blob, Tone.moss, Slot.foliage);
  }
}

function rubble(
  parts: Ruin,
  rng: Rng,
  x: number,
  z: number,
  spread: number,
  count: number,
  y: number,
): void {
  for (let i = 0; i < count; i++) {
    const w = rng.range(0.24, 0.72);
    const h = rng.range(0.16, 0.4);
    const d = rng.range(0.24, 0.66);
    block(
      parts,
      w,
      h,
      d,
      rng.chance(0.4) ? WEATHERED : MARBLE,
      Slot.stone,
      x + rng.range(-spread, spread),
      y + h / 2 - 0.06,
      z + rng.range(-spread, spread),
      rng.range(0, Math.PI),
    );
  }
}

function fallenDrum(parts: Ruin, rng: Rng, x: number, z: number, radius: number): void {
  const length = rng.range(0.9, 1.5);
  const geometry = new THREE.CylinderGeometry(radius, radius, length, 10);
  geometry.rotateZ(Math.PI / 2);
  geometry.rotateY(rng.range(0, Math.PI));
  geometry.translate(x, radius + 0.02, z);
  parts.add(geometry, MARBLE, Slot.stone);
}

function column(
  parts: Ruin,
  rng: Rng,
  x: number,
  z: number,
  base: number,
  height: number,
  radius: number,
): void {
  block(parts, radius * 3.1, 0.2, radius * 3.1, WEATHERED, Slot.stone, x, base + 0.1, z);
  block(parts, radius * 2.5, 0.22, radius * 2.5, MARBLE, Slot.stone, x, base + 0.31, z);
  shaft(parts, x, base + 0.42, z, height, radius, MARBLE);
  const top = base + 0.42 + height;
  block(parts, radius * 2.7, 0.18, radius * 2.7, WEATHERED, Slot.stone, x, top + 0.09, z);
  block(parts, Math.max(radius * 3.0, 0.5), 0.24, Math.max(radius * 3.0, 0.5), MARBLE, Slot.stone, x, top + 0.3, z);
  if (rng.chance(0.4)) moss(parts, rng, x, top + 0.42, z, radius * 1.4, 1);
}

function brokenColumn(
  parts: Ruin,
  rng: Rng,
  x: number,
  z: number,
  base: number,
  height: number,
  radius: number,
): void {
  const stump = height * rng.range(0.24, 0.62);
  block(parts, radius * 3.1, 0.2, radius * 3.1, WEATHERED, Slot.stone, x, base + 0.1, z);
  block(parts, radius * 2.5, 0.22, radius * 2.5, MARBLE, Slot.stone, x, base + 0.31, z);
  shaft(parts, x, base + 0.42, z, stump, radius, MARBLE, rng.range(0.55, 1));
  const top = base + 0.42 + stump;
  for (let i = 0; i < 3; i++) {
    block(
      parts,
      rng.range(0.14, 0.3),
      rng.range(0.12, 0.3),
      rng.range(0.14, 0.3),
      MARBLE,
      Slot.stone,
      x + rng.range(-radius * 0.5, radius * 0.5),
      top + rng.range(0, 0.18),
      z + rng.range(-radius * 0.5, radius * 0.5),
      rng.range(0, Math.PI),
    );
  }
  moss(parts, rng, x, top + 0.06, z, radius * 0.7, 1);
}

function entablature(
  parts: Ruin,
  x: number,
  y: number,
  z: number,
  length: number,
  depth: number,
  yaw: number,
): void {
  block(parts, length, 0.34, depth, MARBLE, Slot.stone, x, y + 0.17, z, yaw);
  block(parts, length, 0.42, depth * 0.94, WEATHERED, Slot.stone, x, y + 0.55, z, yaw);
  block(parts, length * 1.02, 0.26, depth * 1.16, MARBLE, Slot.stone, x, y + 0.89, z, yaw);
}

function rotunda(rng: Rng): THREE.Group {
  const parts = new Ruin();
  const stepTop = 0.44;
  const radius = 0.32;
  const height = 4.0;
  const ring = 4.1;

  const podium = new THREE.CylinderGeometry(5.0, 5.4, 0.44, 24);
  podium.translate(0, 0.22, 0);
  parts.add(podium, DARK_STONE, Slot.stone);
  const upper = new THREE.CylinderGeometry(4.5, 4.9, 0.2, 24);
  upper.translate(0, 0.54, 0);
  parts.add(upper, WEATHERED, Slot.stone);

  const columns = 12;
  for (let i = 0; i < columns; i++) {
    if (i === 3 || i === 8) continue;
    const angle = (i / columns) * Math.PI * 2;
    const x = Math.cos(angle) * ring;
    const z = Math.sin(angle) * ring;
    if (rng.chance(0.25)) brokenColumn(parts, rng, x, z, stepTop, height, radius);
    else column(parts, rng, x, z, stepTop, height, radius);
  }

  for (let i = 0; i < columns; i++) {
    const angle = (i / columns) * Math.PI * 2;
    if (i === 3 || i === 8 || i === 4) continue;
    const x = Math.cos(angle) * ring;
    const z = Math.sin(angle) * ring;
    entablature(parts, x, stepTop + height + 0.42, z, 2.4, 0.9, -angle + Math.PI / 2);
  }

  const drum = new THREE.CylinderGeometry(3.0, 3.1, 2.4, 20, 1, true, 0, Math.PI * 1.72);
  drum.translate(0, stepTop + 4.7, 0);
  parts.add(drum, WEATHERED, Slot.stone);

  const dome = new THREE.SphereGeometry(3.0, 20, 8, 0, Math.PI * 1.7, 0, Math.PI / 2);
  dome.scale(1, 0.78, 1);
  dome.translate(0, stepTop + 5.9, 0);
  parts.add(dome, MARBLE, Slot.stone);

  fallenDrum(parts, rng, -3.4, 2.6, radius);
  fallenDrum(parts, rng, 4.4, -1.1, radius);
  fallenDrum(parts, rng, 0.8, 4.6, radius);
  rubble(parts, rng, 0, 0, 4.6, 18, stepTop - 0.02);
  moss(parts, rng, 0, stepTop - 0.02, 3.4, 2.6, 6);
  moss(parts, rng, -3.8, stepTop - 0.02, -2.4, 2.0, 5);
  moss(parts, rng, 3.6, stepTop - 0.02, 2.2, 1.8, 4);

  return parts.build(0.75);
}

export const BUILDINGS: Array<{
  key: string;
  label: string;
  build: (rng: Rng) => THREE.Group;
  surfaces: LocalSurface[];
}> = [
  {
    key: "rotunda",
    label: "The Rotunda",
    build: rotunda,
    surfaces: [
      { kind: "disc", x: 0, z: 0, radius: 5.35, height: 0.44 },
      { kind: "disc", x: 0, z: 0, radius: 4.62, height: 0.64 },
    ],
  },
];
